import { useAppTheme } from "../../../hooks/useAppTheme";
import React, { useContext, useEffect, useRef, useState } from "react";
import { StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TextInput, TouchableOpacity } from "react-native";
import type { ThemeColors } from "../../../utils/constants/colors";
import { ScrollView } from "react-native-gesture-handler";
import { DataTableWithPagination } from "../../../components/custom/DataTable/DataTableWithPagination";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { Picker } from "react-native";
import { Controller, useForm } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import { DatePickerInput } from "react-native-paper-dates";
import { ThemeProvider, useTheme } from "react-native-paper";
import { flagTrackerOrder } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import {
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

const fullHeaders = {
  MSRef: {
    name: "MS No.",
    width: 0.3,
    fontWeight: 700,
  },
  StateDelivery: {
    name: "State",
    width: 0.2,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Order Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority Order",
    width: 0.3,
    fontWeight: 700,
  },
  ArtworkNo: {
    name: "Artwork No",
    width: 0.3,
    fontWeight: 700,
  },
  ETA: {
    name: "Due Date",
    width: 0.3,
    fontWeight: 700,
  },
  TotalOrdered: {
    name: "Order Qty (UoM)",
    width: 0.3,
    fontWeight: 700,
    textAlign: "right",
  },
  // AusInventory: {
  //   name: "Aus Inventory",
  //   width: 0.3,
  //   fontWeight: 700,
  // },
};

const mediumHeaders = {
  MSRef: {
    name: "MS No.",
    width: 0.4,
    fontWeight: 700,
  },
  StateDelivery: {
    name: "State",
    width: 0.3,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority",
    width: 0.3,
    fontWeight: 700,
  },
  ArtworkNo: {
    name: "Artwork No",
    width: 0.3,
    fontWeight: 700,
  },
  ETA: {
    name: "Due Date",
    width: 0.3,
    fontWeight: 700,
  },
};

const smallHeaders = {
  MSRef: {
    name: "MS No.",
    width: 0.4,
    fontWeight: 700,
  },
  StateDelivery: {
    name: "State",
    width: 0.3,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority",
    width: 0.3,
    fontWeight: 700,
  },
};

const ChinaTrackerFlagModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const listRef = useRef<ScrollView>(null);
  const globalTheme = useTheme();
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height
  );

  const { loadingAddFlag } = useSelector((state: any) => state.chinaTracker);
  const { t } = useTranslation();

  const [isSearched, setIsSearched] = useState<boolean>(false);
  const dispatch = useDispatch<any>();

  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [tableHeaders, setTableHeaders] = useState<any>(fullHeaders);

  const toast = useToast();
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const [, setContentOffSet] = useState<number>(0);

  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  const handleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  useEffect(() => {
    let isMounted: boolean = true;

    setTableHeaders(
      isLargeDevice
        ? translateHeaders(fullHeaders, t, chinaTrackerTranslationMap)
        : isMediumDevice
        ? translateHeaders(mediumHeaders, t, chinaTrackerTranslationMap)
        : translateHeaders(smallHeaders, t, chinaTrackerTranslationMap)
    );
    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isRefresh, isLargeDevice, isMediumDevice, isSmallDevice, screenWidth]);

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "90%";
    if (isMediumDevice) return "80%";
    return "76%";
  };

  // Calculate inner container width based on screen size
  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };
  // Hook form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      subject: "",
      resolvedDate: new Date(),
      message: "",
    },
  });

  const onSubmit = (formData: any) => {
    const payload = {
      key: order.Key,
      orderNumber: order.OrderNumber1,
      subject: formData.subject,
      resolvedDate: formData.resolvedDate.toISOString(), // convert date
      message: formData.message,
    };
    dispatch(flagTrackerOrder(payload))
      .unwrap()
      .then(() => {
        toast.show("Flag created successfully", {
          type: "success",
        });
        onClose();
      })
      .catch((error: any) => {
        toast.show(error?.response?.data || "Error creating flag", {
          type: "danger",
        });
      });
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={[styles.modalContainer, { width: getModalWidth() }]}>
          {loadingAddFlag ? (
            <Spinner
              size="small"
              color="#74BBFF"
              styles={{ flexDirection: "row", margin: "20%" }}
            />
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                { width: getInnerContainerWidth() },
              ]}
            >
              <View style={styles.titleContainer}>
                <Text
                  style={[
                    styles.titleText,
                    isSmallDevice && styles.titleTextSmall,
                  ]}
                >
                  {t("flaggedOrderNumber")}
                </Text>
              </View>

              <ScrollView onScroll={handleScroll} ref={listRef}>
                <View>
                  <View>
                    <View style={styles.containerContent}>
                      <DataTableWithPagination
                        headers={tableHeaders}
                        rows={order ? [order] : []}
                        itemsPerPage={10}
                        isDataLoading={isSearched}
                        isDisplayTimeWithDate={true}
                        useModal={true}
                        isDetail={false}
                        isEdit={false}
                        isDelete={false}
                        // Required props
                        setModalVisible={() => {}}
                        handleRowClick={() => {}}
                        setSelected={() => {}}
                        handleDeleteRow={() => {}}
                        itemTracker={true}
                      />
                    </View>

                    {/* Form fields section */}
                    <View style={styles.formSection}>
                      <View style={styles.formField}>
                        <Text
                          style={[
                            styles.inputLabel,
                            isSmallDevice && styles.inputLabelSmall,
                          ]}
                        >
                          {t("subject")} *
                        </Text>
                        <View style={[styles.picker]}>
                          <Controller
                            control={control}
                            name="subject"
                            rules={{ required: "Subject is required" }}
                            render={({ field: { onChange, value } }) => (
                              <Picker
                                selectedValue={value}
                                onValueChange={onChange}
                                style={[
                                  styles.pickerInput,
                                  isSmallDevice && styles.inputSmall,
                                  {
                                    borderWidth: 0,
                                    backgroundColor: "transparent",
                                    color: colors.text,
                                  },
                                ]}
                                dropdownIconColor={colors.text}
                                mode="dropdown"
                                underlineColorAndroid="transparent"
                              >
                                <Picker.Item
                                  label={t("selectSubject")}
                                  value=""
                                />
                                <Picker.Item
                                  label={t("vesselScheduling")}
                                  value="RESCHEDULE"
                                />
                                <Picker.Item
                                  label={t("machineBroken")}
                                  value="BREAKDOWN"
                                />
                              </Picker>
                            )}
                          />
                        </View>
                        {errors.subject && (
                          <Text style={styles.errorText}>
                            {errors.subject.message as string}
                          </Text>
                        )}
                      </View>

                      <View style={styles.formField}>
                        <Text
                          style={[
                            styles.inputLabel,
                            isSmallDevice && styles.inputLabelSmall,
                          ]}
                        >
                          {t("resolvedDate")} *
                        </Text>
                        <Controller
                          control={control}
                          name="resolvedDate"
                          rules={{
                            required: "Resolved Date is required",
                            pattern: {
                              value: /^\d{2}\/\d{2}\/\d{4}$/, // Validates the format DD/MM/YYYY
                              message: "Please enter a valid date (DD/MM/YYYY)",
                            },
                          }}
                          render={({ field: { onChange, value } }) => (
                            <ThemeProvider theme={{
                                ...globalTheme,
                                dark: true,
                                colors: {
                                  ...globalTheme.colors,
                                  text: colors.text,
                                  onSurfaceVariant: colors.text,
                                  onSurface: colors.text,
                                  primary: colors.tint,
                                  placeholder: colors.text,
                                  background: colors.surface,
                                  surfaceVariant: colors.surfaceSubtle,
                                },
                              }}>
                              <DatePickerInput
                                testID="dateTimePicker"
                                value={new Date()}
                                mode="date"
                                onChange={onChange}
                                style={{ backgroundColor: colors.surface }}
                                textColor={colors.text}
                                withDateFormatInLabel={false}
                                placeholder="DD/MM/YYYY"
                              />
                            </ThemeProvider>
                          )}
                        />
                        {errors.resolvedDate && (
                          <Text style={styles.errorText}>
                            {errors.resolvedDate.message as string}
                          </Text>
                        )}
                      </View>

                      <View style={styles.formField}>
                        <Text
                          style={[
                            styles.inputLabel,
                            isSmallDevice && styles.inputLabelSmall,
                          ]}
                        >
                          {t("message")} *
                        </Text>
                        <Controller
                          control={control}
                          name="message"
                          rules={{ required: "Message is required" }}
                          render={({ field: { onChange, value } }) => (
                            <TextInput
                              value={value}
                              onChangeText={onChange}
                              style={[
                                styles.messageInput,
                                isSmallDevice && styles.messageInputSmall,
                              ]}
                              multiline
                              numberOfLines={isSmallDevice ? 3 : 4}
                            />
                          )}
                        />
                        {errors.message && (
                          <Text style={styles.errorText}>
                            {errors.message.message as string}
                          </Text>
                        )}
                      </View>
                    </View>
                  </View>
                </View>
              </ScrollView>

              <View
                style={[styles.footer, isSmallDevice && styles.footerSmall]}
              >
                <TouchableOpacity
                  onPress={onClose}
                  style={[
                    styles.closeButton,
                    isSmallDevice && styles.buttonSmall,
                  ]}
                >
                  <Text style={[styles.closeButtonText, isSmallDevice && styles.buttonTextSmall]}>
                    {t("close")}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleSubmit(onSubmit)}
                  style={[
                    styles.submitButton,
                    isSmallDevice && styles.buttonSmall,
                  ]}
                >
                  <Text
                    style={[
                      styles.submitText,
                      isSmallDevice && styles.submitTextSmall,
                    ]}
                  >
                    {t("submit")}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  modalContainer: {
    backgroundColor: colors.surface,
    elevation: 8,
    maxHeight: "90%",
  },
  modalContainerSecond: {
    backgroundColor: colors.surface,
    elevation: 8,
    height: "100%",
  },
  titleContainer: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    alignItems: "center",
    backgroundColor: colors.surface,
    paddingVertical: 10,
    marginBottom: 20,
    shadowColor: "#000000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  titleText: {
    fontSize: 25,
    fontWeight: "bold",
    marginLeft: 15,
    color: colors.text,
  },
  titleTextSmall: {
    fontSize: 16,
  },
  formSection: {
    marginTop: 20,
    paddingHorizontal: 35,
  },
  formField: {
    marginBottom: 15,
  },
  input: {
    padding: 10,
    height: 40,
    color: colors.text,
  },
  pickerInput: {
    padding: 10,
    height: 45,
  },
  inputSmall: {
    padding: 5,
    height: 35,
    fontSize: 12,
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: 5,
    color: colors.text,
  },
  inputLabelSmall: {
    fontSize: 12,
    marginBottom: 3,
  },
  picker: {
    borderWidth: 1,
    borderColor: colors.border,
    height: 40,
    backgroundColor: colors.surface,
    borderRadius: 5,
    paddingHorizontal: 10,
    justifyContent: "center",
  },
  pickerSmall: {
    height: 35,
    paddingHorizontal: 5,
    fontSize: 12,
  },
  messageInput: {
    height: 100,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 10,
    color: colors.text,
    backgroundColor: colors.surface,
    marginBottom: 15,
    borderRadius: 5,
    textAlignVertical: "top",
  },
  messageInputSmall: {
    height: 80,
    padding: 5,
    fontSize: 12,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 10,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 5,
  },
  closeButtonText: {
    color: colors.text,
    fontWeight: "600",
  },
  submitButton: {
    backgroundColor: colors.tint,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  submitText: {
    color: colors.surface,
    fontWeight: "bold",
  },
  submitTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 25,
  },
  errorText: {
    color: "red",
    fontSize: 12,
    marginTop: 5,
  },
  });
}

export default ChinaTrackerFlagModal;
